/**
 * Default SplitPay layout — vertical list of order lines with a tap target
 * per row that toggles "you" as a sharer. Long-press / "Split…" link opens
 * the SplitSheet for picking arbitrary N sharers.
 */
import { ClaimPill } from "@/components/group/ClaimPill";
import type { VariationProps } from "./types";
import { lineClaimState } from "./claim-state";

export function ListVariation({
  state,
  selfId,
  lines,
  participantById,
  splits,
  onToggleSelf,
  onOpenSplitSheet,
  format,
}: VariationProps) {
  return (
    <div style={{ padding: "0 16px 12px" }}>
      {lines.map((ln) => {
        const sharers = state.sharers[ln.lineId] ?? [];
        const youOnIt = sharers.includes(selfId);
        const onlyOthers = sharers.length > 0 && !youOnIt;
        const split = sharers.length >= 2;
        const claimedByOther = onlyOthers && sharers.length === 1;
        const otherPayer = claimedByOther
          ? participantById.get(sharers[0])
          : undefined;
        const perShare = sharers.length > 0 ? ln.totalAmount / sharers.length : 0;
        
        const lineSplits = splits.filter((s) => s.claimedLines.some((c) => c.lineId === ln.lineId));
        const claim = lineClaimState(lineSplits, selfId);
        const isLocked = claim.locked;
        const existingPills = claim.pills.map((p, i) => (
          <ClaimPill key={i} kind={p.kind} label={p.label} />
        ));

        return (
          <div
            key={ln.lineId}
            data-testid="split-line"
            data-line-id={ln.lineId}
            data-name={ln.itemName}
            data-amount={ln.totalAmount}
            data-locked={isLocked ? "1" : "0"}
            data-mine={youOnIt ? "1" : "0"}
            onClick={() => {
              if (!isLocked) onToggleSelf(ln.lineId);
            }}
            role={isLocked ? undefined : "button"}
            tabIndex={isLocked ? undefined : 0}
            onKeyDown={(e) => {
              if (isLocked) return;
              if (e.key === "Enter" || e.key === " ") onToggleSelf(ln.lineId);
            }}
            style={{
              display: "flex",
              gap: 12,
              padding: "12px 0",
              borderBottom: "1px solid var(--border)",
              cursor: isLocked ? "default" : "pointer",
              alignItems: "flex-start",
              opacity: isLocked ? 0.6 : 1,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 14,
                  fontWeight: 700,
                  color: "var(--ink-1)",
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  {ln.itemName}
                  {ln.quantity > 1 && (
                    <span
                      style={{
                        marginLeft: 6,
                        fontSize: 12,
                        color: "var(--ink-3)",
                        fontWeight: 600,
                      }}
                    >
                      ×{ln.quantity}
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 14, color: "var(--ink-2)" }}>
                  {format(ln.totalAmount)}
                </span>
              </div>
              <div
                style={{
                  marginTop: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                {existingPills}
                {sharers.length === 0 && existingPills.length === 0 && <ClaimPill kind="unclaimed" />}
                {!split && youOnIt && <ClaimPill kind="you" />}
                {claimedByOther && otherPayer && (
                  <ClaimPill kind="other" payer={otherPayer} />
                )}
                {split && (
                  <ClaimPill
                    kind="split"
                    splitN={1}
                    splitDenom={sharers.length}
                  />
                )}
                {split && perShare > 0 && (
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--ink-3)",
                      fontWeight: 600,
                    }}
                  >
                    {format(perShare)} each
                  </span>
                )}
                {!isLocked && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenSplitSheet(ln.lineId);
                    }}
                    style={{
                      marginLeft: "auto",
                      background: "transparent",
                      border: "none",
                      color: "var(--primary-700)",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    Split…
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
